import { useEffect, useState } from "react";
import cn from "classnames";
import { toast } from "sonner";

import { Icon } from "@trussworks/react-uswds";

// components
import { Radio } from "@trussworks/react-uswds";
import { Button, Input } from "components/LibraryComponents/Components";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// utils
import { dateFormatter, formatAddress } from "utils/helper";
import { saveErrorToast } from "utils/edit-profile";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// styles
import styles from "./ParticipationSection.module.scss";

type ContactPublicType = {
  phone: boolean;
  email: boolean;
  fax: boolean;
  address: boolean;
};

export default function ParticipationSection({
  setUnsavedChanges,
  businessProfileData,
}: SectionsProps) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [signedName, setSignedName] = useState<string>("");
  const [contactPublic, setContactPublic] = useState<ContactPublicType>({
    phone: false,
    email: false,
    fax: false,
    address: false,
  });
  const [savingFields, setSavingFields] = useState<{
    [key: string]: boolean;
  }>({}); // Tracks saving state per field

  const signedDisabled = !signedName.trim();

  const CONTACT_INFORMATION = "Contact information" as const;

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<typeof CONTACT_INFORMATION>();

  const addTask = useMeiliTaskToast();

  // Determine if user has signed based on participation_agreement
  const hasSigned = !!extendedProfile?.participation_agreement;

  const initialSignedName =
    typeof extendedProfile?.participation_agreement === "string"
      ? JSON.parse(extendedProfile?.participation_agreement)?.certifier_name
      : extendedProfile?.participation_agreement?.certifier_name || "";

  const initialSignedDate =
    typeof extendedProfile?.participation_agreement === "string"
      ? JSON.parse(extendedProfile?.participation_agreement)?.date
      : extendedProfile?.participation_agreement?.date || "";

  const contactPublicDisabled =
    contactPublic.phone === (extendedProfile?.display_phone ?? false) &&
    contactPublic.email === (extendedProfile?.display_email ?? false) &&
    contactPublic.fax === (extendedProfile?.display_fax ?? false) &&
    contactPublic.address === (extendedProfile?.display_address ?? false);

  const unsavedChanges = hasSigned
    ? !contactPublicDisabled
    : !signedDisabled || !contactPublicDisabled;

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(unsavedChanges);
    clearConfirmation();
    return () => {
      setUnsavedChanges(false);
    };
  }, [unsavedChanges, setUnsavedChanges]);

  // Initialize state from extendedProfile
  useEffect(() => {
    if (extendedProfile) {
      setSignedName(initialSignedName);
      setContactPublic({
        phone: extendedProfile.display_phone ?? false,
        email: extendedProfile.display_email ?? false,
        fax: extendedProfile.display_fax ?? false,
        address: extendedProfile.display_address ?? false,
      });
    }
  }, [extendedProfile]);

  // Save handler for certification statement
  const handleSaveCertification = async () => {
    if (signedDisabled || !extendedProfile?.uei || savingFields.certification)
      return;

    setSavingFields((prev) => ({ ...prev, certification: true }));
    try {
      const payload = {
        participation_agreement: {
          certifier_name: signedName.trim(),
          date: dateFormatter(new Date().toString(), "MM/DD/YYYY"),
        },
      };

      await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify(payload)
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        participation_agreement: payload.participation_agreement,
      });

      toast.success(`Certification statement saved successfully`);
    } catch (error: any) {
      console.error("Error saving certification statement:", error);
    } finally {
      setSavingFields((prev) => ({ ...prev, certification: false }));
    }
  };

  // Save handler for contact information
  const handleSaveContactPublic = async () => {
    if (
      contactPublicDisabled ||
      !extendedProfile?.uei ||
      savingFields.contactPublic
    )
      return;

    setSavingFields((prev) => ({ ...prev, contactPublic: true }));

    try {
      const payload = {
        display_phone: contactPublic.phone,
        display_email: contactPublic.email,
        display_fax: contactPublic.fax,
        display_address: contactPublic.address,
      };

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify(payload)
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        ...payload,
      });


      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: CONTACT_INFORMATION,
        status: responseData?.status,
      });
      showConfirmation(CONTACT_INFORMATION);
    } catch (error: any) {
      console.error("Error saving contact information:", error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, contactPublic: false }));
    }
  };

  const display = businessProfileData?.public_display_limited
    ? "limited"
    : businessProfileData?.public_display
    ? "public"
    : "none";

  return (
    <>
      <div className={styles["participation-section-root"]}>
        <h2 className={styles["heading"]}>Participation</h2>
        <div
          className={cn(styles["section-content"], styles["space-y-6"], {
            [styles["to-bottom"]]: hasSigned,
          })}
        >
          <div>
            <h3 className={cn(styles["title"], styles["text-black"])}>
              Small Business Certification Statement
            </h3>
            <p className={styles["desc"]}>
              By submitting this form, I certify that:
            </p>

            <ol className={styles["list"]}>
              <li className={styles["list-item"]}>
                The business is a small business under the size standard(s) for
                the listed NAICS code(s) as provided under 13 C.F.R. Part 121;
              </li>
              <li className={styles["list-item"]}>
                This small business, including its principals with primary
                management responsibilities, has not been debarred or suspended
                from furnishing materials, supplies or services to the Federal
                Government;
              </li>
              <li className={styles["list-item"]}>
                The characteristics of the small business’s ownership are
                accurately reflected in the System for Awards Management (SAM)
                profile;
              </li>
              <li className={styles["list-item"]}>
                I have personally reviewed all of the information provided in
                the profile and believe that it is true and correct;
              </li>
              <li className={styles["list-item"]}>
                I am aware that the Small Business Act provides severe penalties
                on parties who knowingly misrepresent their status as a "small
                business concern" in order to obtain for oneself or another
                certain Federal contracts and subcontracts. Such penalties
                include fines of not more than $500,000 or imprisonment for not
                more than 10 years, or both; administrative remedies prescribed
                by the Program Fraud Civil Remedies Act of 1986 (31 U.S.C.
                3801-3812); and suspension and debarment as specified in subpart
                9.4 of title 48, Code of Federal Regulations (or any successor
                regulation)(see 15 U.S.C. 645 16(d)); and
              </li>
              <li className={styles["list-item"]}>
                I am authorized to make this certification on behalf of this
                small business. A red asterisk (*) indicates a required field.
              </li>
            </ol>

            {!hasSigned && (
              <p className={styles["desc-italic"]}>
                A red asterisk (<span>*</span>) indicates a required field.
              </p>
            )}
          </div>

          {hasSigned ? (
            <div className={styles["signed-content"]}>
              <div className={styles["group"]}>
                <p className={styles["label"]}>
                  Certifier name
                </p>
                <p className={styles["value"]}>{initialSignedName}</p>
              </div>
              <div className={styles["group"]}>
                <p className={styles["label"]}>Certification date</p>
                <p className={styles["value"]}>
                  {initialSignedDate}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className={styles["space-y-4"]}>
                <div>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    label="Certifier name"
                    suffix={<span className={styles["asterisk"]}> *</span>}
                    value={signedName}
                    placeholder="Type your first and last name here"
                    onChange={(e) => setSignedName(e.target.value)}
                  ></Input>
                </div>

                <div className={styles["space-y-4"]}>
                  <div className={styles["space-y-2"]}>
                    <p>
                      <strong>Certification Date</strong>
                    </p>
                    <p>
                      {dateFormatter(new Date().toDateString(), "MM/DD/YYYY")}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className={styles["btn-submit"]}
                disabled={signedDisabled || savingFields.certification}
                loading={savingFields.certification}
                onClick={handleSaveCertification}
              >
                {savingFields.certification ? "Saving..." : "Save changes"}
              </Button>
            </>
          )}
        </div>

        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div className={styles["space-y-2"]}>
            <h3 className={styles["title"]}>Search results agreement</h3>
            <div className={styles["space-y-6"]}>
              <p>
                Small Business Search is the SBA’s search engine for small
                businesses that have been registered in SAM.gov. Small business
                certifications by the 8(a), HUBZone, Women-Owned Small Business,
                Economically-Disadvantaged Women-Owned Small Business,
                Veteran-Owned Small Business, or Service-Disabled Veteran-Owned
                Small Business SBA programs are, by law, a matter of public
                record.
              </p>

              {display === "public" ? (
                <Visible />
              ) : display === "limited" ? (
                <LimitedVisible />
              ) : (
                <NotVisible />
              )}
            </div>
          </div>
        </div>

        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div className={styles["space-y-2"]}>
            <h3 className={styles["title"]}>Contact information</h3>
            <p>
              These fields must be edited within SAM.gov. However, you can
              choose to hide them from public search results. Federal government
              users will still be able to see this information.
            </p>
          </div>
          <div className={styles["space-y-6"]}>
            <dl className={styles["space-y-2"]}>
              <dt>Phone number</dt>
              <dd>{businessProfileData?.phone}</dd>
            </dl>
            <div>
              <div className={styles["radio-group"]}>
                <Radio
                  id={"phone-show"}
                  name={"phone"}
                  label={"Show my phone number in public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.phone === true}
                  onChange={() =>
                    setContactPublic((prev) => ({
                      ...contactPublic,
                      phone: true,
                    }))
                  }
                />
                <Radio
                  id={"phone-hide"}
                  name={"phone"}
                  label={"Hide my phone number from public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.phone === false}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, phone: false }))
                  }
                />
              </div>
            </div>

            <dl className={styles["space-y-2"]}>
              <dt>Email address</dt>
              <dd>{businessProfileData?.email}</dd>
            </dl>

            <div>
              <div className={styles["radio-group"]}>
                <Radio
                  id={"email-show"}
                  name={"email"}
                  label={"Show my email address in public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.email === true}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, email: true }))
                  }
                />
                <Radio
                  id={"email-hide"}
                  name={"email"}
                  label={"Hide my email address from public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.email === false}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, email: false }))
                  }
                />
              </div>
            </div>

            <dl className={styles["space-y-2"]}>
              <dt>Address</dt>
              <dd>{formatAddress(businessProfileData)}</dd>
            </dl>

            <div>
              <div className={styles["radio-group"]}>
                <Radio
                  id={"address-show"}
                  name={"address"}
                  label={"Show my address in public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.address === true}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, address: true }))
                  }
                />
                <Radio
                  id={"address-hide"}
                  name={"address"}
                  label={"Hide my address from public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.address === false}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, address: false }))
                  }
                />
              </div>
            </div>

            <dl className={styles["space-y-2"]}>
              <dt>Fax number</dt>
              <dd>{businessProfileData?.fax}</dd>
            </dl>

            <div>
              <div className={styles["radio-group"]}>
                <Radio
                  id={"fax-show"}
                  name={"fax"}
                  label={"Show my fax number in public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.fax === true}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, fax: true }))
                  }
                />
                <Radio
                  id={"fax-hide"}
                  name={"fax"}
                  label={"Hide my fax number from public searches"}
                  className={styles["radio-btn"]}
                  checked={contactPublic.fax === false}
                  onChange={() =>
                    setContactPublic((prev) => ({ ...prev, fax: false }))
                  }
                />
              </div>
            </div>

            <ResponsiveStack>
                <Button
                  type="button"
                  className={styles["btn-submit"]}
                  disabled={contactPublicDisabled || savingFields.contactPublic}
                  loading={savingFields.contactPublic}
                  onClick={handleSaveContactPublic}
                >
                  {savingFields.contactPublic ? "Saving..." : "Save changes"}
                </Button>
                {lastSavedSection === CONTACT_INFORMATION && (
                  <Saved />
                )}
            </ResponsiveStack>
          </div>
        </div>
      </div>
    </>
  );
}

function Visible() {
  return (
    <>
      <p>
        During SAM.gov registration, you did not opt out of this business
        appearing in public search results. To change this, <SamAnchor />.
        Please note that SBA certification information will always be searchable
        within Small Business Search.
      </p>
      <div className={styles["icon-container"]}>
        <Icon.Check height="20" width="20" aria-label="visible icon" />
        <p>Searchable by the public</p>
      </div>
    </>
  );
}

function NotVisible() {
  return (
    <>
      <p>
        During SAM.gov registration, you opted out of this business appearing in
        public search results. To change this, <SamAnchor />. Please note that
        SBA certification information will always be searchable within Small
        Business Search.
      </p>
      <div className={styles["icon-container"]}>
        <Icon.DoNotDisturb
          height="20"
          width="20"
          aria-label="not visible icon"
        />
        <p>Not searchable by the public</p>
      </div>
    </>
  );
}

function LimitedVisible() {
  return (
    <>
      <p>
        During SAM.gov registration, you opted out of this business appearing in
        public search results. However, this small business’s SBA certification
        data will be searchable within a limited view of this business’s profile
        page. To change this status, <SamAnchor />.{" "}
      </p>
      <div className={styles["icon-container"]}>
        <Icon.InfoOutline
          height="20"
          width="20"
          aria-label="limited visible icon"
        />
        <p>Limited profile searchable by the public</p>
      </div>
    </>
  );
}

function SamAnchor() {
  return (
    <a href="https://sam.gov" target="_blank" rel="noopener noreferrer">
      log in to SAM.gov and update your registration
    </a>
  );
}
